import { Box } from '@atgfr8/atg-ui-tailwind';
import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  CalendarMonthIcon,
} from '@atgfr8/icons';
import { BasicModal, BasicTable } from '@components';
import { Column } from '@components/BasicTable/BasicTable';
import Map, { Coordinate } from '@components/Map/Map';
import { useParams } from '@solidjs/router';
import {
  ILatLon,
  IStops,
  fetchCoordinatesFromZip,
  fetchLoadTenderDetails,
  loadTenderStore,
  orderStore,
} from '@store/orders';
import { formatDate } from '@utils/dateFormat';
import { buildPcMilerZipLocation } from '@utils/pcMilerLocation';
import { formatAmount } from '@utils/utils';
import { EDI_LOAD_TENDER_ID } from '@views/order/constants';
import { createEffect, createSignal, onMount } from 'solid-js';

import { IZip } from '../../carrier';
import classes from './classes';

export const EDILoadTender = () => {
  const params = useParams();
  const [coordinates, setCoordinates] = createSignal<Coordinate[]>([]);

  onMount(async () => {
    if (Boolean(orderStore.order.loadTenderId)) {
      await fetchLoadTenderDetails(params.id as string);
    }
  });

  const args = {
    id: EDI_LOAD_TENDER_ID,
    maxWidth: '1605px',
    width: '95vw',
    marginBottom: '10px',
    title: 'Load Tender',
    modalStyles: {
      backgroundColor: 'white',
      overflow: 'hidden',
    },
    footer: false,
    footerContainerClass: 'hidden',
  };

  const columns: Column<IStops>[] = [
    {
      name: 'Date',
      key: 'Date',
      minWidth: 150,
      maxWidth: 150,
      renderCell: (rowData) => (
        <div class={classes.dateContainer}>
          <span>
            {formatDate({
              date: rowData.requestedDate
                ? new Date(rowData.requestedDate)
                : new Date(),
              formatString: 'MM/dd/yyyy',
            })}
          </span>
          <CalendarMonthIcon color="#016FA1" />
        </div>
      ),
    },
    {
      name: 'Stop',
      key: 'Stop',
      minWidth: 80,
      maxWidth: 80,
      renderCell: (rowData) => (
        <div>
          {rowData.dropOff && <ArrowDownCircleIcon color="#016FA1" />}
          {rowData.pickUp && <ArrowUpCircleIcon color="#016FA1" />}
        </div>
      ),
    },
    {
      name: 'Address',
      key: 'Location',
      renderCell: (rowData) => (
        <div class={classes.locationContainer}>
          <div class={classes.marginBottom24}>
            <div>{rowData.address1} </div> <div> {rowData.address2}</div>{' '}
            <div>
              {rowData.city}, {rowData.state}
            </div>{' '}
            <div>
              {rowData.country} {rowData.zip}
            </div>{' '}
          </div>
          <div>
            <div>{rowData.contact.name}</div>{' '}
            <div>{rowData.contact.phone1}</div>
          </div>
        </div>
      ),
    },
    {
      name: 'Time',
      key: 'Time',
      minWidth: 118,
      maxWidth: 118,
      renderCell: (rowData) => (
        <div class={classes.timeContainer}>
          <span>{rowData.requestedTime} </span>
        </div>
      ),
    },
    {
      name: 'Weight',
      key: 'Weight',
      renderCell: (rowData) => (
        <div class={classes.timeContainer}>
          <span>{`${rowData.weight} lbs`}</span>
        </div>
      ),
    },
    {
      name: 'Items',
      key: 'Items',
      renderCell: (rowData) => (
        <div class={classes.itemsContainer}>
          <div>{rowData.items}</div>
        </div>
      ),
    },
    {
      name: 'Description',
      key: 'Description',
      maxWidth: 200,
      renderCell: (rowData) => (
        <div class={classes.descriptionContainer}>{rowData.note}</div>
      ),
    },
  ];

  createEffect(async () => {
    const stops = loadTenderStore.loadTender?.stops;
    if (stops) {
      const zipArray: IZip[] = stops.map((stop) =>
        buildPcMilerZipLocation(stop.zip, stop.country),
      );
      const latlngArray: Coordinate[] = [];

      try {
        const locationArray = await fetchCoordinatesFromZip(zipArray);
        locationArray.forEach((location: ILatLon) => {
          latlngArray.push({
            lat: Number(location.Coords.Lat),
            lng: Number(location.Coords.Lon),
            city: '',
            state: '',
            locationTime: '',
            trackingSource: undefined,
          });
        });
        setCoordinates(latlngArray);
      } catch (error) {
        // Error is ignored
      }
    }
  });

  return (
    <BasicModal showClose={true} {...args} class="border!">
      <div>
        <Box class={classes.flexContainer}>
          <div class={classes.leftContainer}>
            <div class={classes.headingContainer}>
              {loadTenderStore.loadTender?.customer} ( Shipment Id:{' '}
              {loadTenderStore.loadTender?.shipmentId})
            </div>
            <div class={classes.payContainer}>
              Will Pay: {''}
              {formatAmount(Number(loadTenderStore.loadTender?.rate))}
            </div>
            <div class={classes.equipmentContainer}>
              <span class={classes.boldFont}>Equipment</span>:{' '}
              {loadTenderStore.loadTender?.equipment}
            </div>
            <div class={classes.transportationMethod}>
              <span class={classes.boldFont}>
                Transportation Method:{' '}
                {loadTenderStore.loadTender?.transportationMethod}
              </span>
            </div>
            <div class={classes.tableContainer}>
              <BasicTable<IStops>
                columns={columns}
                rows={loadTenderStore.loadTender?.stops}
              />
            </div>
          </div>
          <div class={classes.rightContainer}>
            <Map
              center={[0, 0]}
              height={550}
              width={'100%'}
              style={'basic'}
              coordinates={coordinates()}
              sx={{ 'border-radius': '5px', 'box-shadow': '0px 2px 5px gray' }}
            />
          </div>
        </Box>
      </div>
    </BasicModal>
  );
};
